/**
 * @file src/room/userCards.js
 * @description One public profile lookup per person, shared by everything in a room that shows
 * them. A room holds at most four people, so this is a handful of requests per session — but the
 * waiting room and the chat rail both want the same faces, and without a shared cache the same
 * person is fetched once for their seat and again for every chat row they write.
 *
 * The endpoint (`GET /users/card?userId=`) is the platform's own public user card — the same one
 * the website's hover card reads — so it needs no special auth and returns everything either
 * surface wants: the square avatar, the bio, the follower counts, and the viewer's relationship
 * to that person. It is fetched WHOLE and cached WHOLE: chat only ever wanted the username (to
 * build a profile link) and threw the rest away, which is exactly the payload the seat list needs.
 *
 * A failed or absent lookup is cached as `null` rather than retried: a guest has no card, and a
 * seat that silently re-requests a 404 on every re-render is worse than a letter fallback. The
 * cache is module-scoped and lives for the page — identities do not change mid-session, and the
 * one thing that does (following someone) is written back through `setUserCard`.
 */

import { fetchJsonFromApi } from '../api/httpClient.js';
import { resolveViewerAuth } from '../auth/viewerAuth.js';

/** userId → card object, or `null` for "looked up, there is nothing to show". */
const cards = new Map();
/** userId → in-flight promise, so N callers for the same person make ONE request. */
const inFlight = new Map();
const listeners = new Set();
/**
 * Bumped by `resetUserCards`. Every card carries viewer-relative answers, so a reply that was
 * already in flight when the viewer changed belongs to the previous viewer and is discarded.
 */
let resetGeneration = 0;

function notify() {
  for (const listener of listeners) {
    try {
      listener();
    } catch {
      // A subscriber that throws must not stop the others from hearing about the card.
    }
  }
}

/**
 * The cached card for a user, or null when there is none *or* it has not been fetched yet.
 * Synchronous by design — render seams read it during render and start the fetch in an effect.
 * @param {string|null|undefined} userId
 */
export function getUserCard(userId) {
  if (!userId) return null;
  return cards.get(userId) ?? null;
}

/** Whether this userId has already been resolved (successfully or not) — no card ≠ not tried. */
export function hasUserCard(userId) {
  return Boolean(userId) && cards.has(userId);
}

/**
 * Fetch a user's public card once. Concurrent callers share the single in-flight request, and a
 * resolved userId (card or `null`) is never re-fetched.
 *
 * @param {string|null|undefined} userId
 * @param {{ fetchApi?: typeof fetchJsonFromApi }} [options]
 * @returns {Promise<object|null>}
 */
export function loadUserCard(userId, { fetchApi = fetchJsonFromApi, resolveAuth = resolveViewerAuth } = {}) {
  if (!userId) return Promise.resolve(null);
  if (cards.has(userId)) return Promise.resolve(cards.get(userId));
  const existing = inFlight.get(userId);
  if (existing) return existing;

  const generation = resetGeneration;
  const request = (async () => {
    let card = null;
    // Only a definitive "this person has no card" is worth remembering. A network blip, a 401
    // during auth startup, or a 5xx must stay retryable — caching those would leave a real
    // participant faceless for the whole page.
    let definitive = false;
    try {
      // The viewer's own token: `viewerIsFollowing` is answered RELATIVE to whoever is asking,
      // and an embedded orbiter has no session cookie to speak for it. Without this the card
      // comes back as "not following" for everyone, and the follow button would then unfollow.
      const { authToken } = (await resolveAuth().catch(() => ({}))) ?? {};
      const response = await fetchApi(`/users/card?userId=${encodeURIComponent(userId)}`, { authToken });
      if (response?.ok) {
        const data = await response.json().catch(() => null);
        // The endpoint answers `{ success, user }`; older callers here also tolerated a bare user
        // object, so both shapes are accepted rather than betting on one.
        card = data?.user ?? (data?.userId ? data : null);
        definitive = true;
      } else if (response?.status === 404) {
        definitive = true;
      }
    } catch {
      // Offline or blocked — retryable, so nothing is recorded.
    }
    inFlight.delete(userId);
    // A reset (a logout, a new room) happened while this was in the air: its answer describes a
    // viewer who is no longer here, so it must not repopulate the cache.
    if (generation !== resetGeneration) return card;
    if (definitive) {
      cards.set(userId, card);
      notify();
    }
    return card;
  })();

  inFlight.set(userId, request);
  return request;
}

/**
 * Replace a cached card — for the one thing about a person that CAN change while you are looking
 * at them (following them), so every surface showing that person updates at once.
 * @param {string} userId
 * @param {object|null} card
 */
export function setUserCard(userId, card) {
  if (!userId) return;
  cards.set(userId, card ?? null);
  notify();
}

/** Subscribe to cache changes. Returns an unsubscribe. */
export function subscribeUserCards(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Drop everything. Call this whenever the VIEWER changes (sign in, sign out) — every card carries
 * that viewer's relationship to the person — and on room teardown, so a tab that visits room after
 * room does not accumulate identities for its whole life.
 */
export function resetUserCards() {
  resetGeneration += 1;
  cards.clear();
  inFlight.clear();
  notify();
}
